import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Alert,
  AlertIcon,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FiMapPin, FiCheck, FiLogIn, FiLogOut } from "react-icons/fi";

/**
 * Attendance Check-In/Check-Out component with confirmation dialog.
 * On button click, shows shift details and asks to confirm.
 * "Yes" registers the transaction; "No" offers cancel or message instructor.
 */
export default function AttendanceCheckIn({
  shiftDocId,
  shiftType,
  shiftStart,
  shiftEnd,
  locationName,
  checkInAt,
  checkOutAt,
  checkInWithinGeofence,
  checkOutWithinGeofence,
  checkInDistanceMeters,
  checkOutDistanceMeters,
  onUpdate,
}) {
  const toast = useToast();
  const cancelRef = useRef();
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState(null); // "checkin" | "checkout" | null
  // Decline / message instructor modal
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [instructorMessage, setInstructorMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  // Lab shifts skip attendance entirely
  if (shiftType === "lab") return null;

  const isCheckedIn = !!checkInAt;
  const isCheckedOut = !!checkOutAt;
  const now = new Date();
  const start = shiftStart ? new Date(shiftStart) : null;
  const end = shiftEnd ? new Date(shiftEnd) : null;
  const earlyWindow = start ? new Date(start.getTime() - 30 * 60 * 1000) : null;
  const canCheckIn = !isCheckedIn && earlyWindow && now >= earlyWindow && end && now <= end;
  const canCheckOut = isCheckedIn && !isCheckedOut;

  const formatTime = (dt) => {
    if (!dt) return "—";
    return new Date(dt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        (err) => {
          if (err.code === 1) reject(new Error("Location permission denied. Please enable location access in your browser settings."));
          else if (err.code === 2) reject(new Error("Unable to determine location. Please try again."));
          else if (err.code === 3) reject(new Error("Location request timed out. Please try again."));
          else reject(new Error("Unable to get location"));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });

  const executeCheckIn = useCallback(async () => {
    setLoading(true);
    setLocationError(null);
    setConfirmAction(null);
    try {
      const { latitude, longitude } = await getLocation();
      const response = await fetch("/attendance/checkin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ shiftDocumentationId: shiftDocId, latitude, longitude }),
      });
      const data = await response.json();
      if (data.success) {
        toast({
          title: "Checked In",
          description: data.withinGeofence
            ? `Within geofence (${data.distanceMeters}m from site)`
            : `Recorded — ${data.distanceMeters}m from site (outside geofence)`,
          status: data.withinGeofence ? "success" : "warning",
          duration: 5000,
        });
        onUpdate?.();
      } else {
        toast({ title: "Check-in Failed", description: data.error, status: "error", duration: 5000 });
      }
    } catch (err) {
      setLocationError(err.message);
      toast({ title: "Error", description: err.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [shiftDocId, toast, onUpdate]);

  const executeCheckOut = useCallback(async () => {
    setLoading(true);
    setLocationError(null);
    setConfirmAction(null);
    try {
      const { latitude, longitude } = await getLocation();
      const response = await fetch("/attendance/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ shiftDocumentationId: shiftDocId, latitude, longitude }),
      });
      const data = await response.json();
      if (data.success) {
        toast({
          title: "Checked Out",
          description: `Check-out recorded (${data.distanceMeters}m from site)`,
          status: "success",
          duration: 5000,
        });
        onUpdate?.();
      } else {
        toast({ title: "Check-out Failed", description: data.error, status: "error", duration: 5000 });
      }
    } catch (err) {
      setLocationError(err.message);
      toast({ title: "Error", description: err.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [shiftDocId, toast, onUpdate]);

  const handleDecline = () => {
    setConfirmAction(null);
    setShowDeclineModal(true);
    setInstructorMessage("");
  };

  const handleSendInstructorMessage = useCallback(async () => {
    setSendingMessage(true);
    try {
      const response = await fetch("/attendance/notify-instructor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          shiftDocumentationId: shiftDocId,
          message: instructorMessage || "Student declined to check in/out and needs assistance.",
        }),
      });
      const data = await response.json();
      if (data.success) {
        toast({ title: "Message Sent", description: "Your instructor has been notified.", status: "success", duration: 4000 });
      } else {
        toast({ title: "Notification Sent", description: "Your instructor will be contacted.", status: "info", duration: 4000 });
      }
    } catch {
      // Gracefully handle - the route may not exist yet
      toast({ title: "Noted", description: "Please contact your instructor directly about this shift.", status: "info", duration: 5000 });
    } finally {
      setSendingMessage(false);
      setShowDeclineModal(false);
    }
  }, [shiftDocId, instructorMessage, toast]);

  const actionLabel = confirmAction === "checkin" ? "checking in" : "checking out";
  const confirmTitle = confirmAction === "checkin" ? "Confirm Check-In" : "Confirm Check-Out";

  return (
    <>
      {/* Inline status + action button */}
      <HStack spacing={3} align="center" flexWrap="wrap">
        {canCheckIn && (
          <Button
            leftIcon={<FiLogIn />}
            colorScheme="green"
            size="sm"
            isLoading={loading}
            loadingText="Locating..."
            onClick={() => setConfirmAction("checkin")}
          >
            Check In
          </Button>
        )}
        {canCheckOut && (
          <Button
            leftIcon={<FiLogOut />}
            colorScheme="orange"
            size="sm"
            isLoading={loading}
            loadingText="Locating..."
            onClick={() => setConfirmAction("checkout")}
          >
            Check Out
          </Button>
        )}
        {isCheckedIn && !isCheckedOut && (
          <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
            <HStack spacing={1}><FiCheck size={12} /><Text>In at {formatTime(checkInAt)}</Text></HStack>
          </Badge>
        )}
        {isCheckedIn && isCheckedOut && (
          <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
            <HStack spacing={1}><FiCheck size={12} /><Text>Complete</Text></HStack>
          </Badge>
        )}
        {!canCheckIn && !isCheckedIn && earlyWindow && now < earlyWindow && (
          <Text fontSize="xs" color="gray.500">
            Opens at {formatTime(earlyWindow)}
          </Text>
        )}
        {locationError && (
          <Text fontSize="xs" color="red.400">{locationError}</Text>
        )}
      </HStack>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={!!confirmAction}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmAction(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmTitle}
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={3} align="stretch">
                <Text>
                  Are you {actionLabel} for this shift?
                </Text>
                <Box bg="gray.700" p={3} borderRadius="md">
                  <Text fontWeight="600" mb={1}>{locationName || "Shift"}</Text>
                  <Text fontSize="sm" color="gray.300">
                    {formatTime(shiftStart)} — {formatTime(shiftEnd)}
                  </Text>
                </Box>
                <Text fontSize="sm" color="gray.400">
                  Your GPS location will be recorded for attendance verification.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={handleDecline}
                variant="outline"
                colorScheme="red"
                size="sm"
              >
                No
              </Button>
              <Button
                colorScheme="green"
                onClick={confirmAction === "checkin" ? executeCheckIn : executeCheckOut}
                ml={3}
                size="sm"
                isLoading={loading}
                loadingText="Locating..."
              >
                Yes, {confirmAction === "checkin" ? "Check In" : "Check Out"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Decline / Contact Instructor Modal */}
      <Modal isOpen={showDeclineModal} onClose={() => setShowDeclineModal(false)} isCentered size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Need Help With This Shift?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.300">
                If there is an issue with this shift, you can send a message to your instructor or just cancel.
              </Text>
              <Textarea
                placeholder="Optional: describe the issue (e.g., wrong location, schedule conflict)..."
                value={instructorMessage}
                onChange={(e) => setInstructorMessage(e.target.value)}
                bg="gray.700"
                borderColor="gray.600"
                size="sm"
                rows={3}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeclineModal(false)}
              mr={3}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              isLoading={sendingMessage}
              onClick={handleSendInstructorMessage}
            >
              Notify Instructor
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
